/**
 * Lookup alinhado ao legado (`helpers/uniqueIdLookup.js`):
 * - Giftcard / placa / código com letras: string crua (não strip para dígitos).
 * - Sem '+' e sem letras: só dígitos (CPF) — sem ampliar com '+CPF'.
 * - Com '+': E.164 + variante só dígitos.
 */

import { maskCpf } from '@/shared/lib/input-masks';

/** Mongo/API às vezes devolvem CPF como number — no painel é sempre string. */
export function normalizeUniqueIdAsString(raw: unknown): string {
  if (raw == null) return '';
  return String(raw).trim();
}

/**
 * Só labels de CPF usam máscara/inputMode numérico.
 * Placa, NIF, etc. (`fieldUniqueIdLabel`) ficam como texto livre.
 */
export function isCpfUniqueIdLabel(label?: string | null): boolean {
  const l = (label ?? 'CPF').trim().toLowerCase();
  return l.includes('cpf');
}

/** Exibe uniqueId: máscara CPF quando o label for CPF; senão string crua. */
export function formatUniqueIdForDisplay(raw: unknown, label?: string | null): string {
  const s = normalizeUniqueIdAsString(raw);
  if (!s) return '';
  return isCpfUniqueIdLabel(label) ? maskCpf(s) : s;
}

/** Normaliza `profile.uniqueId` em um lead vindo da API. */
export function normalizeLeadUniqueIdField<T extends { profile?: { uniqueId?: unknown } }>(lead: T): T {
  const raw = lead.profile?.uniqueId;
  if (raw == null || typeof raw === 'string') return lead;
  return {
    ...lead,
    profile: {
      ...lead.profile,
      uniqueId: normalizeUniqueIdAsString(raw),
    },
  };
}

export function uniqueIdCandidates(raw: unknown): string[] {
  if (raw == null) return [];
  const s = normalizeUniqueIdAsString(raw);
  if (!s) return [];

  if (s.startsWith('+')) {
    const e164 = `+${s.slice(1).replace(/\D/g, '')}`;
    const digits = e164.slice(1);
    const out: string[] = [];
    if (e164.length > 1) out.push(e164);
    if (digits) out.push(digits);
    return out;
  }

  // Placa Go Wash etc. — não virar só dígitos (`ABC1D23` → `123`).
  if (/[A-Za-z]/.test(s)) return [s];

  const digits = s.replace(/\D/g, '');
  return digits ? [digits] : [];
}

export function uniqueIdsMatch(stored: string | undefined | null, incoming: unknown): boolean {
  if (stored == null || stored === '') return false;
  const incomingSet = new Set(uniqueIdCandidates(incoming));
  const storedStr = String(stored).trim();
  if (incomingSet.has(storedStr)) return true;
  // Cadastro com '+' e body só dígitos (ou o inverso via candidatos E.164).
  for (const c of uniqueIdCandidates(stored)) {
    if (incomingSet.has(c)) return true;
  }
  // Body dígitos vs stored E.164 (body sem '+' não gera candidato '+').
  if (!storedStr.startsWith('+') && !String(incoming).trim().startsWith('+')) {
    return false;
  }
  const storedDigits = storedStr.replace(/\D/g, '');
  const incomingDigits = String(incoming).replace(/\D/g, '');
  return Boolean(storedDigits && incomingDigits && storedDigits === incomingDigits);
}
